import React, {useState, useCallback} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Button,
    Flex,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Radio,
    RadioGroup,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {CloseIcon} from '../../../components/icons'
import {useGlobalCustomPreference} from '../../../hooks/use-custom-preference'

const AddressText = ({opt, altOpt, isSuggestion}) => {
    // Normalize strings for comparison so we only highlight truly different content
    // (ignore case, surrounding whitespace). This prevents uppercase/lowecase differences
    const normalize = (s) => (typeof s === 'string' ? s.trim().toLowerCase() : s)
    const getWeight = (a, b) => (normalize(a) !== normalize(b) ? 'bold' : 'normal')

    if (isSuggestion) {
        const {Line1, ProvinceName, PostalCode, CountryIso2, City, isManual} = opt
        const provinceCompareTarget = isManual ? altOpt?.stateCode?.value : altOpt?.stateCode?.label
        return (
            <Text>
                <span style={{fontWeight: getWeight(Line1, altOpt?.address1)}}>{Line1}</span>,{' '}
                <span style={{fontWeight: getWeight(City, altOpt?.city)}}>{City}</span>,{' '}
                <span style={{fontWeight: getWeight(ProvinceName, provinceCompareTarget)}}>
                    {ProvinceName}
                </span>
                ,{' '}
                <span style={{fontWeight: getWeight(PostalCode, altOpt?.postalCode)}}>
                    {PostalCode}
                </span>
                , <span>{CountryIso2}</span>
            </Text>
        )
    }
    // For state comparison, use value if manual entry, else use label
    const stateDisplay = opt?.isManual
        ? opt?.stateCode?.value || opt?.stateCode?.label
        : opt?.stateCode?.label
    return (
        <Text>
            {`${opt?.address1}, ${opt?.city}, ${stateDisplay}, ${opt?.postalCode}, ${
                opt?.countryCode?.value || opt?.countryCode
            }`}
        </Text>
    )
}

AddressText.propTypes = {
    opt: PropTypes.shape({
        Line1: PropTypes.string,
        ProvinceName: PropTypes.string,
        PostalCode: PropTypes.string,
        CountryIso2: PropTypes.string,
        City: PropTypes.string,
        isManual: PropTypes.bool,
        address1: PropTypes.string,
        city: PropTypes.string,
        stateCode: PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.string,
        }),
        postalCode: PropTypes.string,
        countryCode: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({value: PropTypes.string}),
        ]),
    }).isRequired,
    altOpt: PropTypes.shape({
        address1: PropTypes.string,
        city: PropTypes.string,
        stateCode: PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.string,
        }),
        postalCode: PropTypes.string,
    }),
    isSuggestion: PropTypes.bool,
}

export const AddressCheckModal = ({
    isOpen,
    closeModal,
    addressesOptions,
    form,
    isInline,
    isManual,
}) => {
    const styles = useMultiStyleConfig('AddressCheckModal', {
        variant: isInline ? 'inline' : 'default',
    })
    const [selectedAddress, setSelectedAddress] = useState(0)
    // Load region configuration preference once (ISOCode -> Name per country)
    const regionsPref = useGlobalCustomPreference('regionConfig')

    const submitSelectedAddress = useCallback(() => {
        const selected = addressesOptions?.[selectedAddress]
        if (selectedAddress !== 0) return closeModal()
        const {Line1, PostalCode, City, ProvinceName, ProvinceCode, CountryIso2} = selected

        // Resolve label from regions preference (case-insensitive match on ISOCode)
        const upperCode = (ProvinceCode || '').toUpperCase()
        const upperName = (ProvinceName || '').toUpperCase()

        const regionList = regionsPref?.[CountryIso2] || []
        const matchedRegion = regionList.find(
            (r) =>
                r?.ISOCode?.toUpperCase() === upperCode ||
                r?.Name?.toUpperCase() === upperCode ||
                r?.Name?.toUpperCase() === upperName,
        )
        const resolvedLabel = isManual
            ? matchedRegion?.Name || ProvinceName || upperCode
            : ProvinceName || matchedRegion?.Name || upperCode

        form.setValue('address1', Line1)
        form.setValue('postalCode', PostalCode)
        form.setValue('city', City)
        form.setValue('c_regionName', ProvinceName)
        form.setValue(
            'stateCode',
            matchedRegion
                ? {
                      value: matchedRegion.ISOCode,
                      label: resolvedLabel,
                  }
                : '',
        )

        closeModal()
    }, [addressesOptions, selectedAddress, form, closeModal, regionsPref])

    const modalContent = (
        <>
            <ModalHeader {...styles.modalHeader}>
                <Text {...styles.modalTitle}>
                    <FormattedMessage
                        defaultMessage="Which delivery address would you like to use?"
                        id="address_check_modal.title"
                    />
                </Text>
                {!isInline && <CloseIcon data-testid="close-modal-button" onClick={closeModal} />}
            </ModalHeader>

            <ModalBody {...styles.modalBody}>
                <Text {...styles.modalDescription}>
                    <FormattedMessage
                        defaultMessage="We may have encountered an issue with the shipping address you entered. Please check and decide which address you want to use."
                        id="address_check_modal.description"
                    />
                </Text>

                <RadioGroup
                    value={selectedAddress}
                    onChange={(val) => setSelectedAddress(Number(val))}
                    {...styles.radioGroupContainer}
                >
                    {addressesOptions?.map((opt, id) => {
                        const isSuggestion = id === 0
                        const altOpt = addressesOptions[1]
                        return (
                            <Flex key={id} {...styles.addressContainer}>
                                <Text {...styles.addressLabel}>
                                    {isSuggestion ? (
                                        <FormattedMessage
                                            id="address_check_modal.verified_address"
                                            defaultMessage="Our suggestion:"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="address_check_modal.alternative_address"
                                            defaultMessage="Your input:"
                                        />
                                    )}
                                </Text>
                                <Radio value={id} mr={4} {...styles.subscriptionEditRadio}>
                                    <AddressText
                                        opt={opt}
                                        altOpt={altOpt}
                                        isSuggestion={isSuggestion}
                                    />
                                </Radio>
                            </Flex>
                        )
                    })}
                </RadioGroup>

                <Text {...styles.editInfoText}>
                    <FormattedMessage
                        defaultMessage="To avoid delays, we recommend using the verified address. Double-check your details to ensure a smooth delivery!"
                        id="address_check_modal.edit_info"
                    />
                </Text>

                <Flex {...styles.buttonContainer}>
                    <Button onClick={closeModal} variant="secondary" width="full">
                        <FormattedMessage
                            defaultMessage="Edit entered address"
                            id="address_check_modal.edit_address"
                        />
                    </Button>
                    <Button
                        onClick={submitSelectedAddress}
                        variant="primary"
                        width="full"
                        isDisabled={!addressesOptions?.length}
                    >
                        <FormattedMessage
                            defaultMessage="Save and continue"
                            id="address_check_modal.save_and_continue"
                        />
                    </Button>
                </Flex>
            </ModalBody>
        </>
    )

    if (isInline) {
        return <Flex direction="column">{modalContent}</Flex>
    }

    return (
        <Modal isOpen={isOpen} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent {...styles.modalContent}>{modalContent}</ModalContent>
        </Modal>
    )
}

AddressCheckModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    closeModal: PropTypes.func.isRequired,
    isInline: PropTypes.bool,
    addressesOptions: PropTypes.arrayOf(
        PropTypes.shape({
            Line1: PropTypes.string,
            PostalCode: PropTypes.string,
            City: PropTypes.string,
            ProvinceName: PropTypes.string,
            ProvinceCode: PropTypes.string,
            CountryIso2: PropTypes.string,
            address1: PropTypes.string,
            city: PropTypes.string,
            stateCode: PropTypes.shape({
                value: PropTypes.string,
                label: PropTypes.string,
            }),
            postalCode: PropTypes.string,
            countryCode: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({value: PropTypes.string}),
            ]),
        }),
    ),
    form: PropTypes.shape({
        setValue: PropTypes.func.isRequired,
    }).isRequired,
    isManual: PropTypes.bool,
}
